import {useMutation, useQueryClient} from '@tanstack/react-query'

import {BSKY_SERVICE} from '#/lib/constants'
import {ASSETS_RQKEY} from '#/state/queries/bills'
import {useAgent} from '#/state/session'
import {type SlideResult} from '#/types/capsule'

export function useCreatePlayMutation() {
  const agent = useAgent()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      capsuleUri,
      score,
      timeMs,
      slideResults,
    }: {
      capsuleUri: string
      score: number
      timeMs: number
      slideResults: SlideResult[]
    }) => {
      const accessJwt = agent.session?.accessJwt
      if (!accessJwt) throw new Error('Not authenticated')

      const res = await fetch(`${BSKY_SERVICE}/xrpc/app.bsky.feed.createPlay`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessJwt}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({capsuleUri, score, timeMs, slideResults}),
      })

      if (!res.ok) {
        const err = await res.json().catch(() => ({}))
        throw new Error(err.message ?? `HTTP ${res.status}`)
      }

      return res.json() as Promise<{playId: string}>
    },
    onSuccess: () => {
      const did = agent.session?.did
      if (!did) return
      void queryClient.invalidateQueries({queryKey: ASSETS_RQKEY(did)})
    },
  })
}

async function postPlay(
  accessJwt: string,
  body: {
    capsuleUri: string
    score: number
    timeMs: number
    slideResults: SlideResult[]
    status: 'started' | 'completed'
  },
) {
  const res = await fetch(`${BSKY_SERVICE}/xrpc/app.bsky.feed.createPlay`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessJwt}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({}))
    throw new Error(err.message ?? `HTTP ${res.status}`)
  }
  return res.json() as Promise<{playId: string}>
}

export function usePlaySubmission() {
  const agent = useAgent()
  const queryClient = useQueryClient()

  const invalidate = () => {
    const did = agent.session?.did
    if (did) void queryClient.invalidateQueries({queryKey: ASSETS_RQKEY(did)})
  }

  const begin = async (capsuleUri: string) => {
    const accessJwt = agent.session?.accessJwt
    if (!accessJwt) return
    await postPlay(accessJwt, {
      capsuleUri,
      score: 0,
      timeMs: 0,
      slideResults: [],
      status: 'started',
    })
  }

  const finish = async ({
    capsuleUri,
    score,
    timeMs,
    slideResults,
  }: {
    capsuleUri: string
    score: number
    timeMs: number
    slideResults: SlideResult[]
  }) => {
    const accessJwt = agent.session?.accessJwt
    if (!accessJwt) return
    await postPlay(accessJwt, {capsuleUri, score, timeMs, slideResults, status: 'completed'})
    invalidate()
  }

  return {begin, finish}
}
